import {
  ShieldCheck,
  ClipboardList,
  ReceiptIndianRupee,
  Megaphone,
  CalendarCheck,
  Lock,
  Users,
  BookOpen,
  Clock,
  Sparkles,
  GraduationCap,
  CreditCard,
  LineChart,
  ClipboardCheck,
  MessageCircle,
  FileText,
  Bus,
  Building2,
  Package,
  Wallet,
  BriefcaseBusiness,
  Library,
  BedDouble,
  PhoneCall,
  IdCard,
} from 'lucide-react';

export const SUPPORT_EMAIL = 'info@cprawat.com';

export const NAV_LINKS = [
  { id: 'features', label: 'Features' },
  { id: 'modules', label: 'Modules' },
  { id: 'roles', label: 'Roles' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'contact', label: 'Contact' },
];

export function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export const HIGHLIGHTS = [
  {
    icon: ReceiptIndianRupee,
    title: 'Fees',
    desc: 'Manage student fees.',
  },
  {
    icon: ClipboardList,
    title: 'Attendance',
    desc: 'Easy marking system.',
  },
  {
    icon: CalendarCheck,
    title: 'Academic year',
    desc: 'Precise and updated.',
  },
  {
    icon: Users,
    title: 'Built for schools',
    desc: 'Manage students, teachers, classes, etc.',
  },
];

export const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Role-based access',
    desc: 'Separate views and permissions for admins, teachers and students.',
  },
  {
    icon: ClipboardList,
    title: 'Attendance',
    desc: 'Daily attendance marking with class/section reporting.',
  },
  {
    icon: ReceiptIndianRupee,
    title: 'Fees & receipts',
    desc: 'Track payments and generate receipts (as configured by school).',
  },
  {
    icon: Megaphone,
    title: 'Announcements',
    desc: 'Publish school-wide announcements and updates.',
  },
  { icon: ClipboardCheck, title: 'Homework', desc: 'Create and track homework with progress and submissions.' },
  { icon: GraduationCap, title: 'Online exams', desc: 'Exam terms, marks entry, results and analytics (if enabled).' },
  { icon: FileText, title: 'Reports', desc: 'Attendance, fee, report cards and exports when you need them.' },
  { icon: MessageCircle, title: 'Communication', desc: 'Announcements, WhatsApp/SMS (optional), and reminders.' },
  { icon: LineChart, title: 'Analytics', desc: 'Get clarity with dashboards and structured insights.' },
];

export const MODULES = [
  { icon: Users, title: 'Students', desc: 'Profiles, class assignment, contact details.' },
  { icon: BriefcaseBusiness, title: 'Teachers', desc: 'Teacher directory, profiles, and assignments.' },
  { icon: ClipboardList, title: 'Attendance', desc: 'Mark attendance and view reports.' },
  { icon: ReceiptIndianRupee, title: 'Fees', desc: 'Payments, pending dues, receipts.' },
  { icon: GraduationCap, title: 'Exams', desc: 'Exam terms, results and analytics (if enabled).' },
  { icon: Clock, title: 'Timetable', desc: 'Class timetable (if configured).' },
  { icon: ClipboardCheck, title: 'Homework', desc: 'Assignments, submissions, and tracking.' },
  { icon: Library, title: 'Library', desc: 'Books, members, issues and returns (optional).' },
  { icon: Bus, title: 'Transport', desc: 'Routes, stops and student allocations (optional).' },
  { icon: BedDouble, title: 'Hostel', desc: 'Hostel management (optional).' },
  { icon: Building2, title: 'Reception', desc: 'Enquiries, visitors, calls and follow-ups (optional).' },
  { icon: Wallet, title: 'Payroll', desc: 'Salary workflows (optional).' },
  { icon: Package, title: 'Inventory', desc: 'Stock and inventory (optional).' },
  { icon: FileText, title: 'Reports', desc: 'Exports, templates and PDFs.' },
  { icon: CalendarCheck, title: 'Calendar', desc: 'Events and holidays.' },
  { icon: Megaphone, title: 'Announcements', desc: 'Broadcast important updates.' },
  { icon: MessageCircle, title: 'SMS', desc: 'Messaging via SMS (optional).' },
  { icon: MessageCircle, title: 'WhatsApp', desc: 'Broadcast and logs (optional).' },
];

export const ROLES = [
  {
    title: 'Super Admin',
    theme: 'blue',
    icon: Sparkles,
    bullets: ['Platform setup', 'Manage schools & plans', 'CRM & onboarding', 'Global analytics & controls'],
  },
  {
    title: 'School Admin',
    theme: 'blue',
    icon: ShieldCheck,
    bullets: ['School configuration', 'Students, staff & classes', 'Fees & receipts', 'Reports & exports'],
  },
  {
    title: 'Teacher',
    theme: 'green',
    icon: BriefcaseBusiness,
    bullets: [
      'Attendance & class workflows',
      'View student data as permitted',
      'Announcements and timetable',
      'Homework and exam management',
    ],
  },
  {
    title: 'Student',
    theme: 'neutral',
    icon: Users,
    bullets: [
      'Own profile & timetable',
      'View announcements',
      'Fees and results (if enabled)',
      'Homework and class updates',
    ],
  },
  {
    title: 'Parent',
    theme: 'neutral',
    icon: Users,
    bullets: ['View child’s attendance', 'Fees & receipts', 'Announcements & messages', 'Leave and updates'],
  },
  {
    title: 'Accountant',
    theme: 'blue',
    icon: CreditCard,
    bullets: ['Fee collection', 'Receipts & exports', 'Dues and follow-ups', 'Reports & reconciliation'],
  },
  {
    title: 'Receptionist',
    theme: 'green',
    icon: PhoneCall,
    bullets: ['Visitor log', 'Phone calls', 'Enquiries & follow-ups', 'Daily reports'],
  },
  {
    title: 'Librarian',
    theme: 'neutral',
    icon: BookOpen,
    bullets: ['Books & members', 'Issue/return workflows', 'Overdue tracking', 'Reports'],
  },
];

export const ROLE_CTA_LABEL = 'View Permissions';

export const ROLE_THEMES = {
  blue: {
    card: 'border-blue-500/30 bg-gradient-to-br from-blue-600/20 to-blue-900/10',
    bullet: 'text-blue-400',
    button: 'border-blue-500/40 text-blue-300 hover:bg-blue-600/20',
  },
  green: {
    card: 'border-emerald-500/30 bg-gradient-to-br from-emerald-600/20 to-emerald-900/10',
    bullet: 'text-emerald-400',
    button: 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/20',
  },
  neutral: {
    card: 'border-white/10 bg-white/5',
    bullet: 'text-sky-400',
    button: 'border-white/20 text-white/80 hover:bg-white/10',
  },
};

export const STATS = [
  { icon: Building2, label: 'Schools', value: 120 },
  { icon: Users, label: 'Students', value: 24000 },
  { icon: BriefcaseBusiness, label: 'Teachers', value: 1800 },
  { icon: IdCard, label: 'Parents', value: 32000 },
];

export const FAQ_ITEMS = [
  {
    q: 'How does School Analytics work?',
    a: 'School Analytics is a secure web portal that centralizes attendance, fees, academics, and communication in one place—tailored to each role.',
  },
  {
    q: 'How can schools subscribe?',
    a: 'Choose a plan and billing cycle, then complete checkout. Your school admin account will be upgraded instantly after successful payment.',
  },
  {
    q: 'Is data secure?',
    a: 'We use authenticated access, role-based permissions, and secure API communication. Your school data stays isolated by school account.',
  },
  {
    q: 'Can I upgrade later?',
    a: 'Yes. You can upgrade your plan anytime from the subscription settings page.',
  },
  {
    q: 'Do you offer support?',
    a: 'Yes. Contact us via the form on this page and our team will respond promptly.',
  },
];
